import { useEffect, useRef, useState } from "react";
import { MessageCircle, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/shared/components/EmptyState";
import { ErrorState } from "@/shared/components/ErrorState";
import { LoadingState } from "@/shared/components/LoadingState";
import { MessageListSkeleton } from "@/shared/components/Skeletons";
import { MessageBubble, type Message } from "./MessageBubble";
import { cn } from "@/shared/utils/cn";

interface MessagesPanelProps {
  title?: string;
  messages: Message[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
  isFetching?: boolean;
  /** Leave extra bottom padding so bubbles won’t sit under the footer */
  reserveComposerSpace?: boolean;
  /** Height cap for internal scrolling (e.g. "50vh", "420px") */
  maxHeight?: string;
}

export function MessagesPanel({
  title = "Messages",
  messages,
  isLoading,
  isError,
  error,
  isFetching = false,
  reserveComposerSpace = false,
  maxHeight = "60vh",
}: MessagesPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [didInitScroll, setDidInitScroll] = useState(false);
  const [nearBottom, setNearBottom] = useState(true);

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;

    const atTop = scrollTop <= 1;                       // <- exact top
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    setShowScrollToTop(!atTop);
    setShowScrollToBottom(!atBottom);
    setNearBottom(atBottom);
  };

  // Initial snap to bottom; later, only if user is near bottom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!didInitScroll) {
      el.scrollTop = el.scrollHeight; // instant, no animation
      setDidInitScroll(true);
      requestAnimationFrame(handleScroll);
      return;
    }

    if (nearBottom) {
      el.scrollTop = el.scrollHeight; // keep pinned when new items append
    }
    requestAnimationFrame(handleScroll);
  }, [messages, didInitScroll, nearBottom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = (e: Event) => {
      e.stopPropagation();
      handleScroll();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    requestAnimationFrame(handleScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollToBottom = () => {
    const el = containerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  // Loading / error shells
  if (isLoading || (isFetching && messages.length === 0)) {
    return (
      <div className="flex flex-col h-full w-full" data-testid="messages-panel">
        <div className="flex items-center justify-between p-2 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground mt-1 mb-1">{title}</h2>
        </div>
        <div className="flex-1 overflow-y-auto relative min-h-0 scrollbar-hide w-full" style={{ maxHeight }}>
          <MessageListSkeleton />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col h-full w-full" data-testid="messages-panel">
        <div className="flex items-center justify-between p-2 border-b border-border flex-shrink-0">
          <h2 className="text-lg font-semibold text-foreground mt-1 mb-1">{title}</h2>
        </div>
        <div className="flex-1 flex items-center justify-center w-full" style={{ maxHeight }}>
          <ErrorState
            title="Failed to load messages"
            description={error instanceof Error ? error.message : "An error occurred while loading messages"}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col h-full min-h-0 w-full max-w-full overflow-y-auto relative scrollbar-hide"
      data-testid="messages-panel"
      ref={containerRef}
    >
      {/* sticky header */}
      <div className="flex items-center justify-between p-2 border-b border-border sticky top-0 z-10 bg-background">
        <h2 className="text-lg font-semibold text-foreground mt-1 mb-1">{title}</h2>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <MessageCircle className="h-4 w-4" />
          <span data-testid="message-count">{messages.length} messages</span>
        </div>
      </div>
  
      {/* Scrollable content */}
      <div
        className={cn("p-4 lg:pl-6 lg:pr-6 space-y-1 w-full", reserveComposerSpace && "pb-composer")}
        data-testid="messages-container"
      >
        {/* TOP sticky helper lives INSIDE the scrollable content */}
        <div className="sticky top-16 z-10 pointer-events-none">
          <div className="flex justify-end pr-1">
            <Button
              variant="outline"
              size="sm"
              onClick={scrollToTop}
              className={cn(
                "pointer-events-auto rounded-full shadow-md transition-opacity duration-150",
                // soft translucency so it doesn't block content
                "bg-background/70 hover:bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60",
                "border border-border",
                showScrollToTop ? "opacity-100" : "opacity-0"
              )}
              aria-label="Scroll to top"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
          </div>
        </div>
  
        {/* messages */}
        {isFetching && messages.length > 0 && (
          <div className="mb-4">
            <LoadingState
              title="Updating messages..."
              description="Fetching latest messages"
              size="sm"
              className="py-2"
            />
          </div>
        )}
  
        {messages.length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title="No messages yet"
            description="Start the conversation by sending a message below."
          />
        ) : (
          messages.map((m, i) => (
            <MessageBubble key={m.id} message={m} previousMessage={messages[i - 1]} />
          ))
        )}
  
        {/* BOTTOM sticky helper, offset above composer */}
        <div
          className="sticky z-10 pointer-events-none"
          style={{ bottom: "calc(var(--composer-height, 88px) + 0.5rem)" }}
        >
          <div className="flex justify-end pr-1">
            <Button
              variant="outline"
              size="sm"
              onClick={scrollToBottom}
              className={cn(
                "pointer-events-auto rounded-full shadow-md transition-opacity duration-150",
                "bg-background/70 hover:bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/60",
                "border border-border",
                showScrollToBottom ? "opacity-100" : "opacity-0"
              )}
              aria-label="Scroll to bottom"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
